import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DoneIcon from '@material-ui/icons/Done';
import FeedbackIcon from '@material-ui/icons/Feedback';
import { graphql } from 'babel-plugin-relay/macro';
import { get } from 'lodash';

import { SvgIconWrapper, SvgIconWrapperProps, Tooltip, Typography } from 'components/v2';

import { IconMessageStatus_Message$key } from './__generated__/IconMessageStatus_Message.graphql';

const messages = defineMessages({
  delivered: {
    id: 'deprecated.fragments.IconMessageStatus.delivered',
    defaultMessage: 'Delivered',
  },
  sent: {
    id: 'deprecated.fragments.IconMessageStatus.sent',
    defaultMessage: 'Sent',
  },
  undelivered: {
    id: 'deprecated.fragments.IconMessageStatus.undelivered',
    defaultMessage: 'Undelivered',
  },
  statusReason: {
    id: 'deprecated.fragments.IconMessageStatus.statusReason',
    defaultMessage: 'Reason: {statusReason}',
  },
});

export interface IconMessageStatusProps extends Omit<SvgIconWrapperProps, 'classes' | 'Icon' | 'color'> {
  fragmentRef: IconMessageStatus_Message$key;
  status?: string | null;
}

const IconMessageStatus: React.FC<IconMessageStatusProps> = ({ fragmentRef, ...rest }) => {
  const message = useFragment<IconMessageStatus_Message$key>(
    graphql`
      fragment IconMessageStatus_Message on Message {
        status
        statusReason
        externalStatus {
          reason
        }
      }
    `,
    fragmentRef,
  );

  const { status } = message;
  const { formatMessage } = useIntl();
  const statusReason = useMemo(() => get(message, 'externalStatus.reason', message.statusReason), [message]);

  if (status === 'delivered' || status === 'opened') {
    return (
      <Tooltip title={formatMessage(messages.delivered)} placement="top" hideArrow>
        <SvgIconWrapper Icon={CheckCircleIcon} color="success" {...rest} />
      </Tooltip>
    );
  }
  if (status === 'sent' || status === 'pending') {
    return (
      <Tooltip title={formatMessage(messages.sent)} placement="top" hideArrow>
        <SvgIconWrapper Icon={DoneIcon} color="success" {...rest} />
      </Tooltip>
    );
  }
  if (status === 'undelivered') {
    return (
      <Tooltip
        title={
          <>
            <Typography color="inherit">{formatMessage(messages.undelivered)}</Typography>
            {statusReason && (
              <Typography color="inherit">
                <i>{formatMessage(messages.statusReason, { statusReason })}</i>
              </Typography>
            )}
          </>
        }
        placement="top"
        hideArrow
      >
        <SvgIconWrapper Icon={FeedbackIcon} color="error" {...rest} />
      </Tooltip>
    );
  }
  return null;
};

export default IconMessageStatus;
